import { useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { DownloadIcon, PlayIcon } from "lucide-react";

import {
  AudioPlayer,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { Button } from "@ui/button";
import { TableCell, TableRow } from "@ui/table";

import { useGetCallSignedAudioUrl } from "@/hooks/queries/useGetCallSignedAudioUrl";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { misc } from "@/lib/misc";
import { SadieCallResponse } from "@/types";

type AudioAction = "play" | "download";

// Each row owns its own audio element so players don't fight over one ref.
export const SadieVoiceCallRow = ({ call }: { call: SadieCallResponse }) => {
  /* React Hooks */
  const audioRef = useRef<HTMLAudioElement>(null);
  // The signed URL is only fetched once someone plays or downloads the call.
  const [pendingAction, setPendingAction] = useState<AudioAction | null>(null);
  const [isAudioRequested, setIsAudioRequested] = useState(false);

  /* Database CRUD Operations */
  // Read
  const hasAudio = call.audioUrl !== null;
  // Calls store a bucket path; playback needs a short-lived signed URL.
  const audioQuery = useGetCallSignedAudioUrl({
    callId: call.id,
    enabled: hasAudio && isAudioRequested,
  });
  const signedAudioUrl = audioQuery.data;

  // Finish the play or download that triggered the fetch.
  useEffect(() => {
    if (!signedAudioUrl || !pendingAction) return;
    if (pendingAction === "download") {
      saveAs(signedAudioUrl, `sadie-call-${call.id}.mp3`);
    } else {
      void audioRef.current?.play().catch(() => undefined);
    }
    setPendingAction(null);
  }, [call.id, pendingAction, signedAudioUrl]);

  /* Helpers */
  const fromLabel = call.from
    ? misc.formatPhoneNumberOnCopy(call.from.replace(/\D+/g, ""))
    : "—";

  /* Handlers */
  const handleRequestAudio = (action: AudioAction) => {
    setIsAudioRequested(true);
    setPendingAction(action);
  };

  const handlePlay = () => {
    mixpanelUtils.trackButtonClick(
      "Play/Pause",
      getScreenFromPathname(location.pathname),
    );
    handleRequestAudio("play");
  };

  const handleDownload = () => {
    handleRequestAudio("download");
  };

  /* Render */
  const renderPlayback = () => {
    if (!hasAudio) return <Text className="text-slate-400">No audio</Text>;
    if (!isAudioRequested || audioQuery.isPending) {
      return (
        <IdlePlayer
          duration={call.duration}
          isLoading={isAudioRequested}
          onPlay={handlePlay}
        />
      );
    }
    if (audioQuery.isError)
      return <ErrorText errorMessage="Error loading audio." />;
    return <AudioPlayer audioRef={audioRef} audioSrc={audioQuery.data} />;
  };

  return (
    <TableRow>
      <TableCell>
        <Text className="text-slate-900">{fromLabel}</Text>
      </TableCell>
      <TableCell>
        <Text className="text-slate-500">
          {dates.formatShortDateTime(call.startedAt)}
        </Text>
      </TableCell>
      <TableCell>
        <Text className="text-slate-500">
          {call.duration !== null
            ? formatting.callDuration(call.duration)
            : "—"}
        </Text>
      </TableCell>
      <TableCell>{renderPlayback()}</TableCell>
      <TableCell>
        <Button
          aria-label="Download call recording"
          disabled={!hasAudio || pendingAction === "download"}
          onClick={handleDownload}
          size="icon"
          variant="ghost"
        >
          <DownloadIcon className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

// Stand-in that matches AudioPlayer's layout until the signed URL is fetched.
const IdlePlayer = ({
  duration,
  isLoading,
  onPlay,
}: {
  duration: number | null;
  isLoading: boolean;
  onPlay: () => void;
}) => {
  return (
    <Row className="items-center gap-3 rounded-lg pl-2">
      <Button
        className="h-7 w-7 shrink-0 text-slate-700 hover:bg-transparent hover:text-slate-900"
        disabled={isLoading}
        onClick={onPlay}
        size="icon"
        variant="ghost"
      >
        {isLoading ? (
          <LoadingIndicator variant="small" />
        ) : (
          <PlayIcon className="h-4 w-4 fill-current" />
        )}
      </Button>
      <Text className="w-[72px] shrink-0 text-xs font-normal text-slate-500">
        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
        0:00 / {formatting.callDuration(duration || 0)}
      </Text>
      <Row className="h-1.5 flex-1 rounded-full bg-slate-200" />
    </Row>
  );
};
